/**
 * RdvRoom — Salle de visioconférence sécurisée CONFILEX (Phase 1 MVP).
 *
 * URL : /rdv/:accessToken
 *
 * Trois états :
 *   - status="requested"  → salle d'attente (en attente de confirmation spécialiste)
 *   - status="confirmed"  → salle active : iframe Jitsi public
 *   - status="cancelled"  → message d'annulation
 *   - status="completed"  → message de clôture + lien retour dossier
 *
 * Le service de visio est fourni par Jitsi Public (meet.jit.si). Aucune
 * authentification supplémentaire — l'URL signée du RDV suffit comme secret.
 *
 * Phase 2 (à venir) : Daily.co ou 100ms pour enregistrement, transcription
 * Whisper, et résumé automatique post-visio.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Video, Calendar, Clock, AlertTriangle, CheckCircle2, ExternalLink,
  XCircle, Sparkles, Loader2,
} from "lucide-react";
import { apiClient } from "@/lib/api";

const POLL_INTERVAL_MS = 6000;

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function RdvRoom() {
  const { accessToken } = useParams();
  const [appt, setAppt] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollerRef = useRef(null);

  const load = async () => {
    try {
      const r = await apiClient.get(`/appointments/${accessToken}`);
      setAppt(r.data);
      setLoading(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Rendez-vous introuvable.");
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => {
      if (pollerRef.current) clearInterval(pollerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // Poll while status is 'requested' to detect spécialiste confirmation
  useEffect(() => {
    if (!appt) return undefined;
    if (appt.status === "requested") {
      pollerRef.current = setInterval(load, POLL_INTERVAL_MS);
      return () => clearInterval(pollerRef.current);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appt?.status]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#C9A961] animate-spin" />
      </div>
    );
  }

  if (error || !appt) {
    return (
      <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md shadow-sm">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">CONFILEX</p>
          <h1 className="font-serif italic text-2xl text-[#0B3A2A] mt-2">{error || "Rendez-vous introuvable."}</h1>
          <Link to="/" className="text-sm text-[#8A6E3A] mt-4 inline-block hover:underline">
            ← Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  // ---- Cancelled / Completed ----
  if (appt.status === "cancelled" || appt.status === "completed") {
    return (
      <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center p-6"
           data-testid={`rdv-${appt.status}`}>
        <div className="bg-white rounded-2xl p-8 text-center max-w-md shadow-sm">
          {appt.status === "cancelled"
            ? <XCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
            : <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />}
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">CONFILEX</p>
          <h1 className="font-serif italic text-2xl text-[#0B3A2A] mt-2">
            {appt.status === "cancelled" ? "Rendez-vous annulé" : "Visioconférence terminée"}
          </h1>
          <p className="text-[13px] text-slate-600 italic mt-3 leading-relaxed">
            {appt.status === "cancelled"
              ? `Le rendez-vous avec ${appt.expert_name} a été annulé. Vous pouvez en demander un nouveau depuis sa fiche.`
              : `Merci d'avoir échangé avec ${appt.expert_name}. Un compte-rendu pourra être ajouté à votre dossier prochainement.`}
          </p>
          {appt.workspace_id && (
            <Link to="/" className="text-sm text-[#8A6E3A] mt-5 inline-block hover:underline">
              ← Retour à mon dossier
            </Link>
          )}
        </div>
      </div>
    );
  }

  // ---- Waiting room (status=requested) ----
  if (appt.status === "requested") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0B3A2A] to-[#0B3A2A] text-white"
           data-testid="rdv-waiting-room">
        <div className="max-w-2xl mx-auto px-6 py-16">
          <p className="text-[10px] uppercase tracking-[0.32em] text-[#C9A961]">CONFILEX · SALLE D'ATTENTE</p>
          <h1 className="font-serif italic text-3xl sm:text-4xl mt-3 leading-tight">
            En attente de confirmation
          </h1>
          <p className="text-sm text-white/75 mt-3 leading-relaxed">
            Votre demande de rendez-vous avec <b>{appt.expert_name}</b> a bien
            été envoyée. La salle visio sera activée dès que le spécialiste aura
            validé le créneau.
          </p>

          <div className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl p-5 mt-6 space-y-3">
            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-[#C9A961]" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/60">Créneau demandé</p>
                <p className="text-sm text-white">{formatDateTime(appt.requested_at)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-[#C9A961]" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/60">Durée</p>
                <p className="text-sm text-white">{appt.duration_min} min</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Sparkles className="w-4 h-4 text-[#C9A961] mt-1" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/60">Motif</p>
                <p className="text-sm text-white">{appt.subject}</p>
              </div>
            </div>
          </div>

          <p className="text-[11px] italic text-white/60 mt-6 leading-relaxed">
            Cette page se mettra à jour automatiquement dès la confirmation.
            Vous pouvez quitter — la salle vous attendra à la même URL.
          </p>

          <div className="mt-4 flex items-center gap-2 text-white/60">
            <Loader2 className="w-4 h-4 animate-spin text-[#C9A961]" />
            <span className="text-[12px] italic">Vérification en temps réel…</span>
          </div>
        </div>
      </div>
    );
  }

  // ---- Confirmed → embed Jitsi ----
  return (
    <div className="min-h-screen bg-black flex flex-col" data-testid="rdv-room-active">
      <header className="bg-[#0B3A2A] text-white px-5 py-3 flex items-center justify-between gap-3 shrink-0">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">CONFILEX · SALLE SÉCURISÉE</p>
          <p className="text-sm truncate">
            {appt.expert_name} <span className="text-white/60">— {appt.subject}</span>
          </p>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/appointments/${accessToken}/ics`}
          target="_blank" rel="noreferrer"
          data-testid="rdv-ics-link"
          className="text-[11px] text-[#C9A961] hover:underline inline-flex items-center gap-1 shrink-0"
        >
          <Calendar className="w-3 h-3" /> .ics
        </a>
      </header>
      <iframe
        src={`${appt.meeting_url}#config.disableDeepLinking=true&config.prejoinPageEnabled=false`}
        title="Visioconférence CONFILEX"
        allow="camera; microphone; fullscreen; display-capture; autoplay"
        data-testid="rdv-jitsi-iframe"
        className="flex-1 w-full bg-black border-0"
      />
      <footer className="bg-[#0B3A2A] text-white/70 text-[10px] px-5 py-2 italic shrink-0 leading-relaxed">
        Service de visioconférence fourni par Jitsi (public). CONFILEX assure une infrastructure de mise en relation —
        les échanges restent confidentiels entre les participants. Cette visioconférence ne constitue ni une consultation
        juridique, ni un conseil fiscal, financier ou comptable.
      </footer>
    </div>
  );
}
